import type { KpiCardSpec } from '~containers/analytics/kpis/card-spec';

/**
 * 31DOC dashboard cards (DOC31-99): the RPP dashboard's sections and
 * indicators, read for one 31DOC edition, minus what has no meaning in 31DOC.
 *
 * Left out on purpose, so nobody puts them back:
 * - Sessions per user: it counts browser sign-ins, and 31DOC runs only inside
 *   the app, so it would always read 0.
 * - Total opened game sessions, Completion rate and Drop-off rate: a 31DOC
 *   play is one door opening, done in one step, with no session opened without
 *   playing, no round and nothing to abandon.
 * - The whole Technical health section: 31DOC has no Sentry project of its own
 *   to read error rate and failed sessions from.
 * - Watch ads: 31DOC has no such bonus action, so its row never appears.
 *
 * Every card reads the page's edition (`campaignId`), and every player count
 * is read in the participation's province snapshot.
 */

/** Section 1: Overall performance. */
export const OVERALL_PERFORMANCE_CARDS: KpiCardSpec[] = [
    {
        // Named like its RPP twin, "RPP registered users", which is the name
        // the business knows the card by. The number is the visiting one.
        title: '31DOC registered users',
        source: 'players-31doc',
        form: 'number',
        metric: 'activeUsers31doc',
        size: 'sm',
        description:
            'Registered players who opened 31DOC during the selected period, counted once each however many times they came back. A player who enrolled earlier and came back counts; one who enrolled without coming back in the period does not.',
    },
    {
        title: 'Weekly active users',
        source: 'players-31doc',
        form: 'number',
        metric: 'weeklyActiveUsers31doc',
        size: 'sm',
        description:
            'Players who opened 31DOC during the 7 days ending on the last day of the selected period.',
    },
    {
        title: 'Daily active users',
        source: 'players-31doc',
        form: 'number',
        metric: 'dailyActiveUsers31doc',
        size: 'sm',
        description:
            'Players who opened 31DOC on the last day of the selected period.',
    },
    {
        title: '31DOC unique players',
        source: 'players-31doc',
        form: 'number',
        metric: 'newPlayers31doc',
        size: 'sm',
        description:
            'Players who enrolled in 31DOC during the selected period.',
    },
    {
        title: 'Repeat visit rate',
        source: 'players-31doc',
        form: 'rate',
        metric: 'repeatVisitRate31doc',
        size: 'sm',
        description:
            'Share of the players who opened 31DOC in the selected period who came back on a second day or more. A day counts once however many times the player came back that day.',
    },
];

/** Section 2: Business impact, all national. */
export const BUSINESS_IMPACT_CARDS: KpiCardSpec[] = [
    {
        title: 'In-store traffic / individuals',
        source: 'purchases-31doc',
        metric: 'individuals',
        form: 'number',
        size: 'sm',
        description:
            'Players with at least one in-store purchase Six12 credited to 31DOC during the selected period.',
    },
    {
        title: 'Revenue via LIFT',
        source: 'purchases-31doc',
        metric: 'revenue',
        form: 'number',
        size: 'sm',
        description:
            'Revenue of the in-store purchases Six12 credited to 31DOC, measured through LIFT.',
    },
    {
        title: 'No. of lift transactions',
        source: 'purchases-31doc',
        metric: 'transactions',
        form: 'number',
        size: 'sm',
        description:
            'In-store purchases Six12 credited to 31DOC. Entries without a purchase (NPE) are not counted.',
    },
    {
        title: 'Avg. spent by transaction',
        source: 'purchases-31doc',
        metric: 'avgSpend',
        form: 'number',
        size: 'sm',
        description: 'Average amount spent per credited purchase.',
    },
    {
        title: 'Items in basket',
        source: 'purchases-31doc',
        metric: 'itemsPerBasket',
        form: 'number',
        size: 'sm',
        description: 'Average number of items per credited purchase.',
    },
];

/** Section 3: Game engagement. A 31DOC play is a door opening. */
export const GAME_ENGAGEMENT_CARDS: KpiCardSpec[] = [
    {
        title: 'Total games played',
        source: 'door-openings-31doc',
        form: 'number',
        metric: 'doorsOpened31doc',
        size: 'sm',
        description:
            'Doors opened during the selected period. A player opens at most one door a day.',
    },
    {
        title: 'Average plays per user',
        source: 'door-openings-31doc',
        form: 'number',
        metric: 'openingsPerPlayer31doc',
        size: 'sm',
        description:
            'Doors opened divided by the players who opened at least one door during the selected period.',
    },
];

/** Section 4a: Coupons, the two headline counts. */
export const COUPON_CARDS: KpiCardSpec[] = [
    {
        title: 'Coupons issued',
        source: 'coupons-31doc',
        form: 'number',
        metric: 'couponsIssued31doc',
        size: 'sm',
        description:
            'Coupons won by opening a door during the selected period, counted in the region the winner enrolled in. A coupon later gifted to another player still counts once, for its winner.',
    },
    {
        title: 'Coupons redeemed',
        source: 'coupons-31doc',
        form: 'number',
        metric: 'couponsRedeemed31doc',
        size: 'sm',
        description:
            'Coupons won by opening a door and redeemed in store during the selected period, whenever they were won. A gifted coupon redeemed by the player it was gifted to counts for its winner.',
    },
];

/** Section 4b: the same two counts across the four business units. */
export const COUPON_BREAKDOWN_CARDS: KpiCardSpec[] = [
    {
        title: 'Issued by business unit',
        source: 'coupons-31doc',
        form: 'pie',
        metric: 'couponsIssued31doc',
        breakdownBy: 'businessUnit',
        size: 'sm',
        description:
            'The same coupons issued, split across the four business units. The slices sum to the headline count.',
    },
    {
        title: 'Redeemed by business unit',
        source: 'coupons-31doc',
        form: 'pie',
        metric: 'couponsRedeemed31doc',
        breakdownBy: 'businessUnit',
        size: 'sm',
        description:
            'The same coupons redeemed, split across the four business units. The slices sum to the headline count.',
    },
];

/** Section 5: Engagement drivers, badges and bonus actions, national. */
export const ENGAGEMENT_DRIVER_CARDS: KpiCardSpec[] = [
    {
        title: 'Badges',
        source: 'engagement-drivers-31doc',
        form: 'bars',
        metric: 'badgeEarnRate',
        breakdownBy: 'badge',
        size: 'sm',
        description:
            'Share of the players active during the selected period who earned each badge in it. One bar per 31DOC badge.',
        barBaseInHeader: true,
    },
    {
        title: 'Bonus actions',
        source: 'engagement-drivers-31doc',
        form: 'bars',
        metric: 'bonusCompletionRate',
        breakdownBy: 'bonusAction',
        size: 'sm',
        description:
            'Share of the players active during the selected period who completed each bonus action in it. One bar per 31DOC action; the three social networks are one bar.',
        barBaseInHeader: true,
    },
];
